// ============================================================================
// src/lib/feedReceiptOperationApi.ts — the client of public.feed_operation_fenced
// ----------------------------------------------------------------------------
// Migration 260 (proposed; Conductor v69 wire) adds ONE fenced entrypoint in
// front of the feed writes whose legacy replies settle nothing: a legacy
// `no_change`, `refused` or value `conflict` is answered without a receipt, so
// a delayed copy of the same request id can still land after a newer edit
// (outbox 78). The wrapper binds the exact intent under its own request lock,
// delegates to the existing operation in the SAME transaction, and stores an
// immutable receipt for EVERY terminal outcome before it returns.
//
// What this module guarantees its callers:
//   • a frozen, JSON-exact request: family + operation from the closed v69
//     map, complete targets/expected/change, and the expected actor captured
//     when the intent was frozen. An invalid request is refused HERE, before
//     any send (`not_sent`), which is a different thing from every outcome
//     after a send.
//   • retries of a lost answer re-send the byte-identical request under the
//     same id, and only ever into this fence.
//   • a reply settles only when it binds THIS request completely (request id,
//     receipt id, actor, family, operation, targets, expected, change,
//     canonical version) and carries a stored outcome. Anything else — a
//     legacy-shaped body, a partial or foreign binding — settles nothing.
//   • a raised 42501 (role / expected actor), any other raised SQLSTATE, a
//     transport failure and a missing wrapper never settle. There is NO
//     fallback to a legacy RPC: an absent wrapper fails closed.
//   • history: an own receipt read back settles exactly like a reply; an
//     absent receipt is NOT a settlement.
//
// Deployment gate: consumers adopt this module only after 260 is live on the
// target (release order 258 -> 259 -> 260 -> clients). Until then every call
// is `unavailable`, which keeps the draft and settles nothing.
// ============================================================================

import {newClientSubmissionId} from './clientSubmissionId.js';
import type {SupabaseClient} from './supabaseTypes.js';

export const FEED_FENCE_RPC = 'feed_operation_fenced' as const;
export const FEED_FENCE_RECEIPT_RPC = 'feed_operation_fenced_receipt' as const;
export const FEED_FENCE_CANONICAL_VERSION = 1 as const;

// ---------------------------------------------------------------------------
// The closed family/operation map (v69). Nothing outside it can be sent.
// ---------------------------------------------------------------------------
export const FEED_CONFIG_KEYS = Object.freeze({
  'feed_orders.patch': 'ppp-feed-orders-v1',
  'poultry_feed_inventory.save': 'ppp-poultry-feed-inventory-v1',
  'pig_feed_inventory.save': 'ppp-pig-feed-inventory-v1',
  'feed_costs.save': 'ppp-feed-costs-v1',
} as const);
export const FEED_LAYER_GROUPS_KEY = 'ppp-layer-groups-v1' as const;

export type JsonValue = null | boolean | number | string | JsonValue[] | {[key: string]: JsonValue};
export type JsonObject = {[key: string]: JsonValue};
type Empty = Record<string, never>;

export type FeedConfigOperation = keyof typeof FEED_CONFIG_KEYS;
export type FeedFenceIntent =
  | {family: 'config'; operation: FeedConfigOperation; targets: {key: string}; expected: Empty; change: JsonObject}
  | {
      family: 'broiler';
      operation: 'batch.update';
      targets: {batch_id: string};
      expected: JsonObject;
      change: JsonObject;
    }
  | {family: 'layer'; operation: 'batch.feed_rates'; targets: Empty; expected: Empty; change: JsonObject}
  | {
      family: 'app_store';
      operation: 'layer_groups.save';
      targets: {key: string};
      expected: {updated_at: string | null};
      change: {document: JsonValue[]};
    }
  | {family: 'pig'; operation: 'feeder.feed_rate'; targets: Empty; expected: Empty; change: {rate: number}};
export type FeedFenceFamily = FeedFenceIntent['family'];

/** A request as it is sent and compared: deep-frozen, JSON-exact. */
export type FeedFenceRequest = Readonly<
  FeedFenceIntent & {
    requestId: string;
    /** The draft owner's subject, captured when the intent was frozen. */
    expectedActor: string;
  }
>;

export type FeedFenceOutcomeKind = 'applied' | 'no_change' | 'conflict' | 'refused';
const OUTCOME_KINDS: readonly string[] = ['applied', 'no_change', 'conflict', 'refused'];

/** The stored, bound settlement of one request. */
export type FeedFenceReceipt = Readonly<{
  requestId: string;
  receiptId: string;
  actor: string;
  family: FeedFenceFamily;
  operation: string;
  targets: JsonObject;
  expected: JsonObject;
  change: JsonObject;
  canonicalVersion: typeof FEED_FENCE_CANONICAL_VERSION;
  outcome: FeedFenceOutcomeKind;
  replayed: boolean;
  detail: JsonValue;
}>;

export type FeedFenceOutcome =
  /** The ONLY settlement: a stored receipt binding this exact request. */
  | Readonly<{status: 'settled'; receipt: FeedFenceReceipt}>
  /** Refused before any send: nothing reached a server. */
  | Readonly<{status: 'not_sent'; problem: string}>
  /** 42501: role or expected actor. Never settles; the intent is held. */
  | Readonly<{status: 'denied'; code: string; message: string}>
  /** Any other raised SQLSTATE. Never settles; the intent is held. */
  | Readonly<{status: 'rejected'; code: string; message: string}>
  /** The fenced entrypoint is not there. Fails closed; never a legacy call. */
  | Readonly<{status: 'unavailable'; message: string}>
  /** No bound answer (transport, timeout, a legacy or foreign reply). */
  | Readonly<{status: 'unknown'; message: string}>;

export type FeedFenceHistory = FeedFenceOutcome | Readonly<{status: 'absent'}>;

/** The one settlement predicate. `absent`, `denied`, `unknown`, ... never pass. */
export function isFeedFenceSettled(
  outcome: FeedFenceHistory,
): outcome is Readonly<{status: 'settled'; receipt: FeedFenceReceipt}> {
  return outcome.status === 'settled';
}

// ---------------------------------------------------------------------------
// Freezing: the request is validated and normalized BEFORE any send.
// ---------------------------------------------------------------------------
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const REQUEST_ID = /^[A-Za-z0-9._:-]{8,128}$/;

/** A fresh request id for ONE new intent under the fence. The prefix only marks
 * fence-era ids for a reader; it proves nothing about provenance. Adoption
 * starts NEW intents only: legacy-era held work is never re-fenced. */
export function newFeedFenceRequestId(prefix = 'feed'): string {
  return `w260-${prefix}-${newClientSubmissionId()}`;
}

function isPlainObject(value: unknown): value is {[key: string]: unknown} {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/** Why `value` is not exact JSON (NaN, Infinity, undefined, a Date, ...), or null. */
function jsonProblem(value: unknown, at: string): string | null {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? null : `${at} is not a finite number`;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const p = jsonProblem(value[i], `${at}[${i}]`);
      if (p) return p;
    }
    return null;
  }
  if (isPlainObject(value)) {
    for (const [k, v] of Object.entries(value)) {
      const p = jsonProblem(v, `${at}.${k}`);
      if (p) return p;
    }
    return null;
  }
  return `${at} is not JSON`;
}

const keysAre = (value: {[key: string]: unknown}, keys: readonly string[]) => {
  const own = Object.keys(value).sort();
  return own.length === keys.length && [...keys].sort().every((k, i) => own[i] === k);
};

/** Why this intent may not be sent under the fence, or null. */
export function feedFenceIntentProblem(input: unknown): string | null {
  if (!isPlainObject(input)) return 'the intent is not an object';
  const {family, operation, targets, expected, change} = input;
  for (const [name, part] of [
    ['targets', targets],
    ['expected', expected],
    ['change', change],
  ] as const) {
    if (!isPlainObject(part)) return `${name} must be a JSON object`;
    const p = jsonProblem(part, name);
    if (p) return p;
  }
  const t = targets as {[key: string]: unknown};
  const e = expected as {[key: string]: unknown};
  const c = change as {[key: string]: unknown};
  switch (family) {
    case 'config': {
      if (typeof operation !== 'string' || !Object.hasOwn(FEED_CONFIG_KEYS, operation))
        return `config does not admit ${String(operation)}`;
      const key = FEED_CONFIG_KEYS[operation as FeedConfigOperation];
      if (!keysAre(t, ['key']) || t.key !== key) return `config ${operation} targets must be exactly {"key": "${key}"}`;
      if (!keysAre(e, [])) return 'config expected must be exactly {}';
      return null;
    }
    case 'broiler':
      if (operation !== 'batch.update') return `broiler does not admit ${String(operation)}`;
      if (!keysAre(t, ['batch_id']) || typeof t.batch_id !== 'string' || t.batch_id === '') {
        return 'broiler batch.update targets must be exactly {"batch_id": "<id>"}';
      }
      if (!Object.keys(e).length)
        return 'broiler batch.update expected must be the complete frozen expected, never empty';
      if (!Object.keys(c).length) return 'broiler batch.update change is empty';
      return null;
    case 'layer':
      if (operation !== 'batch.feed_rates') return `layer does not admit ${String(operation)}`;
      if (!keysAre(t, []) || !keysAre(e, [])) return 'layer batch.feed_rates targets and expected must be exactly {}';
      if (!Object.keys(c).length) return 'layer batch.feed_rates change is empty';
      return null;
    case 'app_store':
      if (operation !== 'layer_groups.save') return `app_store does not admit ${String(operation)}`;
      if (!keysAre(t, ['key']) || t.key !== FEED_LAYER_GROUPS_KEY) {
        return `app_store layer_groups.save targets must be exactly {"key": "${FEED_LAYER_GROUPS_KEY}"}`;
      }
      if (!keysAre(e, ['updated_at']) || (e.updated_at !== null && typeof e.updated_at !== 'string')) {
        return 'app_store layer_groups.save expected must be exactly {"updated_at": string | null}';
      }
      if (!keysAre(c, ['document']) || !Array.isArray(c.document))
        return 'app_store layer_groups.save change must be exactly {"document": [...]}';
      return null;
    case 'pig':
      if (operation !== 'feeder.feed_rate') return `pig does not admit ${String(operation)}`;
      if (!keysAre(t, []) || !keysAre(e, [])) return 'pig feeder.feed_rate targets and expected must be exactly {}';
      if (!keysAre(c, ['rate']) || typeof c.rate !== 'number')
        return 'pig feeder.feed_rate change must be exactly the legacy {"rate": number}';
      return null;
    default:
      return `unknown family ${String(family)}`;
  }
}

// Every request freezeFeedFenceRequest issued (and only those): validated,
// deep-copied and deep-frozen, so every attempt and every binding uses exactly
// the original. Anything else — a hand-built or partly frozen object, a stored
// copy — is refused until it is re-issued through freezeFeedFenceRequest.
const ISSUED = new WeakSet<object>();

function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object') {
    for (const v of Object.values(value as object)) deepFreeze(v);
    Object.freeze(value);
  }
  return value;
}

/**
 * Freeze one intent for the fence: validated, JSON-normalized and deep-frozen,
 * with its request id and expected actor. The result is what is sent on every
 * attempt and what every reply is compared against. It is also the ONLY way
 * back in for a stored request (rehydration): pass the stored fields again.
 */
export function freezeFeedFenceRequest(
  intent: FeedFenceIntent,
  {requestId, expectedActor}: {requestId: string; expectedActor: string},
): {ok: true; request: FeedFenceRequest} | {ok: false; problem: string} {
  const problem = feedFenceIntentProblem(intent);
  if (problem) return {ok: false, problem};
  if (typeof requestId !== 'string' || !REQUEST_ID.test(requestId))
    return {ok: false, problem: 'request id must be 8..128 safe characters'};
  if (typeof expectedActor !== 'string' || !UUID.test(expectedActor))
    return {ok: false, problem: 'expected actor must be a UUID'};
  const exact = JSON.parse(
    JSON.stringify({
      family: intent.family,
      operation: intent.operation,
      targets: intent.targets,
      expected: intent.expected,
      change: intent.change,
    }),
  ) as FeedFenceIntent;
  const request = deepFreeze({...exact, requestId, expectedActor} as FeedFenceRequest);
  ISSUED.add(request);
  return {ok: true, request};
}

// ---------------------------------------------------------------------------
// Binding: a reply settles only when it is THIS request's stored outcome.
// ---------------------------------------------------------------------------
/** jsonb structural equality: object key order is irrelevant, array order is not. */
export function sameJsonb(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((x, i) => sameJsonb(x, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const ka = Object.keys(a);
    if (ka.length !== Object.keys(b).length) return false;
    return ka.every((k) => Object.hasOwn(b, k) && sameJsonb(a[k], b[k]));
  }
  return false;
}

/** The stored receipt this reply carries for `request`, or why it is not one. */
export function bindFeedFenceReply(
  request: FeedFenceRequest,
  data: unknown,
): {ok: true; receipt: FeedFenceReceipt} | {ok: false; problem: string} {
  if (!isPlainObject(data)) return {ok: false, problem: 'the reply is not an object'};
  const d = data;
  if (!('receipt_id' in d) || !('outcome' in d))
    return {ok: false, problem: 'a reply without a fenced receipt (legacy shape?)'};
  const mismatch = (
    [
      ['request_id', d.request_id === request.requestId],
      ['actor', d.actor === request.expectedActor],
      ['family', d.family === request.family],
      ['operation', d.operation === request.operation],
      ['targets', sameJsonb(d.targets, request.targets)],
      ['expected', sameJsonb(d.expected, request.expected)],
      ['change', sameJsonb(d.change, request.change)],
      ['canonical_version', d.canonical_version === FEED_FENCE_CANONICAL_VERSION],
      ['receipt_id', typeof d.receipt_id === 'string' && UUID.test(d.receipt_id)],
      ['outcome', typeof d.outcome === 'string' && OUTCOME_KINDS.includes(d.outcome)],
      ['replayed', typeof d.replayed === 'boolean'],
      ['detail', Object.hasOwn(d, 'detail') && jsonProblem(d.detail, 'detail') === null],
    ] as const
  ).find(([, ok]) => !ok);
  if (mismatch) return {ok: false, problem: `the reply does not bind this request (${mismatch[0]})`};
  return {
    ok: true,
    receipt: deepFreeze({
      requestId: request.requestId,
      receiptId: d.receipt_id as string,
      actor: request.expectedActor,
      family: request.family,
      operation: request.operation,
      targets: request.targets as JsonObject,
      expected: request.expected as JsonObject,
      change: request.change as JsonObject,
      canonicalVersion: FEED_FENCE_CANONICAL_VERSION,
      outcome: d.outcome as FeedFenceOutcomeKind,
      replayed: d.replayed as boolean,
      detail: (d.detail ?? null) as JsonValue,
    }),
  };
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------
const ATTEMPTS = 3;
const ATTEMPT_TIMEOUT_MS = 8000;
const SQLSTATE = /^[0-9A-Z]{5}$/;
// PostgREST's "function not in the schema cache" and PostgreSQL's
// undefined_function: the wrapper is not deployed on this target.
const MISSING_FUNCTION = new Set(['PGRST202', '42883']);

type RpcError = {code?: string; message?: string} | null;

/** A raised error, classified; null for a lost answer (retry the same request). */
function classifyRpcError(error: NonNullable<RpcError>): FeedFenceOutcome | null {
  const code = typeof error.code === 'string' ? error.code : '';
  const message = error.message || 'The server did not accept this request.';
  if (MISSING_FUNCTION.has(code))
    return {status: 'unavailable', message: 'The fenced feed save is not available on this server.'};
  if (code === '42501') return {status: 'denied', code, message};
  if (SQLSTATE.test(code)) return {status: 'rejected', code, message};
  if (code) return {status: 'unknown', message};
  return null;
}

export type FeedFenceSendOptions = {
  /** Checked immediately before each RPC attempt. If false on attempt 1,
   * returns `not_sent` (nothing reached the wire). If false on attempt 2+,
   * returns `unknown` (attempt 1 already left the browser, so the request
   * remains held for the original owner without sending a retry under a
   * changed session/owner). */
  beforeAttempt?: () => Promise<boolean> | boolean;
};

async function callWithRetries(
  call: () => PromiseLike<{data: unknown; error: RpcError}>,
  answer: (data: unknown) => FeedFenceHistory,
  options?: FeedFenceSendOptions,
): Promise<FeedFenceHistory> {
  let lastMessage = 'No reply from the server.';
  for (let attempt = 1; attempt <= ATTEMPTS; attempt++) {
    if (options?.beforeAttempt) {
      let allowed = false;
      try {
        allowed = Boolean(await options.beforeAttempt());
      } catch {
        allowed = false;
      }
      if (!allowed) {
        return attempt === 1
          ? {status: 'not_sent', problem: 'the writer is no longer current before sending'}
          : {
              status: 'unknown',
              message: 'Could not confirm this save after the active account changed. Your change is kept; retry.',
            };
      }
    }
    let timer: ReturnType<typeof setTimeout> | null = null;
    try {
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), ATTEMPT_TIMEOUT_MS);
      });
      const {data, error} = (await Promise.race([call(), timeout])) as {data: unknown; error: RpcError};
      if (error) {
        const classified = classifyRpcError(error);
        if (classified) return classified;
        throw new Error(error.message || 'transport');
      }
      return answer(data);
    } catch (e) {
      lastMessage = (e as {message?: string}).message || lastMessage;
      if (attempt < ATTEMPTS) await new Promise((r) => setTimeout(r, 1000 * attempt));
    } finally {
      if (timer !== null) clearTimeout(timer);
    }
  }
  return {status: 'unknown', message: `Could not confirm this save (${lastMessage}). Your change is kept; retry.`};
}

/** Null only for a request freezeFeedFenceRequest issued (validated, deep-frozen). */
function issuedRequestProblem(request: FeedFenceRequest): string | null {
  return request && typeof request === 'object' && ISSUED.has(request)
    ? null
    : 'the request was not issued by freezeFeedFenceRequest (re-issue a stored request through it)';
}

/** The exact parameters of one fenced send: identical on every attempt. */
export function feedFenceParams(request: FeedFenceRequest) {
  return Object.freeze({
    p_family: request.family,
    p_request_id: request.requestId,
    p_operation: request.operation,
    p_targets: request.targets,
    p_expected: request.expected,
    p_change: request.change,
    p_expected_actor: request.expectedActor,
  });
}

/**
 * Send ONE frozen request through the fence. Never throws. A lost answer is
 * re-sent unchanged under the same id (into this fence only); a raised error
 * and an unbound reply settle nothing.
 */
export async function applyFeedFenceOperation(
  sb: SupabaseClient,
  request: FeedFenceRequest,
  options?: FeedFenceSendOptions,
): Promise<FeedFenceOutcome> {
  const problem = issuedRequestProblem(request);
  if (problem) return {status: 'not_sent', problem};
  const params = feedFenceParams(request);
  const out = await callWithRetries(
    // The RPC names are literals (the physical call-site inventory reads them);
    // the exported constants name the same two functions for callers and tests.
    () => sb.rpc('feed_operation_fenced', params) as unknown as PromiseLike<{data: unknown; error: RpcError}>,
    (data) => {
      const bound = bindFeedFenceReply(request, data);
      return bound.ok ? {status: 'settled', receipt: bound.receipt} : {status: 'unknown', message: bound.problem};
    },
    options,
  );
  // A mutation always answers; only the history read may be absent.
  return out.status === 'absent' ? {status: 'unknown', message: 'no reply body'} : out;
}

/**
 * Read back this request's OWN receipt, for the caller's current authority.
 * A found receipt settles exactly like a reply; an absent one does NOT.
 */
export async function readFeedFenceReceipt(
  sb: SupabaseClient,
  request: FeedFenceRequest,
  options?: FeedFenceSendOptions,
): Promise<FeedFenceHistory> {
  const problem = issuedRequestProblem(request);
  if (problem) return {status: 'not_sent', problem};
  return callWithRetries(
    () =>
      sb.rpc('feed_operation_fenced_receipt', {
        p_request_id: request.requestId,
        p_expected_actor: request.expectedActor,
      }) as unknown as PromiseLike<{data: unknown; error: RpcError}>,
    (data) => {
      if (data === null || data === undefined) return {status: 'absent'};
      const bound = bindFeedFenceReply(request, data);
      return bound.ok ? {status: 'settled', receipt: bound.receipt} : {status: 'unknown', message: bound.problem};
    },
    options,
  );
}
